import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import SaveIcon from '@mui/icons-material/Save';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import GradeOutlinedIcon from '@mui/icons-material/GradeOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/History';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getActiveTermIndex } from '../utils/termUtils';

const fieldSx = { width: '100%' };

export function SettingsPageHeader() {
    return (
        <Paper
            elevation={0}
            sx={{
                mb: 3,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, #1760d3 0%, #17dddd 100%)',
                color: '#fff',
            }}
        >
            <Toolbar sx={{ py: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                        School settings
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
                        Manage your school profile, academic calendar, grading scale, and terms.
                    </Typography>
                </Box>
            </Toolbar>
        </Paper>
    );
}

export function SettingsSchoolProfileForm({
    person,
    imagehandler,
    logoFallbackSrc,
    onLogoError,
    schoolname,
    schooladdress,
    schoolcontact,
    yearestablished,
    studentpopulation,
    staffpopulation,
    email,
    country,
    loading,
    changer,
    setschoolname,
    setschooladdress,
    setschoolcontact,
    setyearestablished,
    setstudentpopulation,
    setstaffpopulation,
    setemail,
    setcountry,
    onSubmit,
}) {
    return (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardHeader
                avatar={<SchoolOutlinedIcon color="primary" />}
                title="School profile"
                subheader="Logo, contact details, and country (affects currency on Pricing)."
            />
            <CardContent>
                <form onSubmit={onSubmit}>
                    <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
                        <input style={{ display: 'none' }} id="fileimage" type="file" accept="image/*" onChange={imagehandler} />
                        <label htmlFor="fileimage" style={{ cursor: 'pointer' }}>
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={
                                    <CameraAltIcon
                                        sx={{
                                            bgcolor: 'primary.main',
                                            color: '#fff',
                                            p: 0.75,
                                            borderRadius: '50%',
                                            fontSize: 22,
                                        }}
                                    />
                                }
                            >
                                <Avatar
                                    src={person || logoFallbackSrc || undefined}
                                    alt="School logo"
                                    imgProps={{
                                        onError: (e) => {
                                            if (typeof onLogoError === 'function') {
                                                onLogoError(e);
                                                return;
                                            }
                                            const img = e.currentTarget;
                                            img.onerror = null;
                                            if (logoFallbackSrc) img.src = logoFallbackSrc;
                                            else img.removeAttribute('src');
                                        },
                                    }}
                                    sx={{ width: 112, height: 112, border: '3px solid', borderColor: 'grey.200' }}
                                />
                            </Badge>
                        </label>
                        <Typography variant="caption" color="text.secondary">
                            Tap to change school logo
                        </Typography>
                    </Stack>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                id="schoolname"
                                label="School name"
                                value={schoolname}
                                onChange={(e) => changer(e, setschoolname)}
                                size="small"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                id="email"
                                label="Email"
                                type="email"
                                value={email}
                                onChange={(e) => changer(e, setemail)}
                                size="small"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id="address"
                                label="Address"
                                value={schooladdress}
                                onChange={(e) => changer(e, setschooladdress)}
                                size="small"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                id="contact"
                                label="Contact phone"
                                type="tel"
                                value={schoolcontact}
                                onChange={(e) => changer(e, setschoolcontact)}
                                size="small"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                id="year"
                                label="Year established"
                                type="number"
                                value={yearestablished}
                                onChange={(e) => changer(e, setyearestablished)}
                                size="small"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel id="country-label">Country</InputLabel>
                                <Select
                                    labelId="country-label"
                                    id="country"
                                    value={country}
                                    label="Country"
                                    onChange={(e) => setcountry(e.target.value)}
                                >
                                    <MenuItem value="GH">Ghana</MenuItem>
                                    <MenuItem value="LR">Liberia</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <FormControl size="small" fullWidth>
                                <InputLabel id="school-size-label">School size (students)</InputLabel>
                                <Select
                                    id="students"
                                    labelId="school-size-label"
                                    value={studentpopulation}
                                    label="School size (students)"
                                    onChange={(e) => setstudentpopulation(e.target.value)}
                                >
                                    <MenuItem value="none">
                                        <em>Choose school size</em>
                                    </MenuItem>
                                    {['50-200', '201-400', '401-600', '601-800', '801-1000', '1001-1200', '1201-1400', '1401-1600', '1601-1800', '1801-2000'].map(
                                        (band) => (
                                            <MenuItem key={band} value={band}>
                                                {band}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                id="staff"
                                label="Staff size"
                                type="number"
                                value={staffpopulation}
                                onChange={(e) => changer(e, setstaffpopulation)}
                                size="small"
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                        {!loading ? (
                            <Button type="submit" variant="contained" size="large" startIcon={<SaveIcon />}>
                                Save school profile
                            </Button>
                        ) : (
                            <LoadingButton loading variant="contained" size="large" startIcon={<SaveIcon />}>
                                Saving…
                            </LoadingButton>
                        )}
                    </Stack>
                </form>
            </CardContent>
        </Card>
    );
}

export function SettingsReopenDateCard({
    schooldetails_,
    months,
    reopendate,
    setreopendate,
    loading,
    onSetDate,
}) {
    const reopenLabel =
        schooldetails_.length !== 0 && schooldetails_[0].reopendate !== ''
            ? (() => {
                  const p = schooldetails_[0].reopendate.split('-');
                  return `${months[parseInt(p[1], 10) - 1]} ${p[2]}, ${p[0]}`;
              })()
            : 'Not set';

    return (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardHeader
                avatar={<CalendarMonthOutlinedIcon color="primary" />}
                title="Re-opening date"
                subheader={`Current: ${reopenLabel}`}
            />
            <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <TextField
                        id="reopendate"
                        type="date"
                        value={reopendate}
                        onChange={(e) => setreopendate(e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        label="New re-opening date"
                        sx={{ ...fieldSx, maxWidth: 280 }}
                    />
                    {!loading ? (
                        <Button variant="outlined" onClick={onSetDate}>
                            Update date
                        </Button>
                    ) : (
                        <LoadingButton loading variant="outlined">
                            Updating…
                        </LoadingButton>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

export function SettingsGradingSection({
    grades,
    openGradesDialog,
    setOpenGradesDialog,
    editingGradingScales,
    updateBand,
    removeBand,
    addGradeBand,
    handleSaveSchoolGrading,
    savingGrades,
}) {
    return (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardHeader
                avatar={<GradeOutlinedIcon color="primary" />}
                title="Grading system"
                subheader="Score bands used on report cards and academic records."
                action={
                    <Button variant="contained" size="small" onClick={() => setOpenGradesDialog(true)}>
                        Customize scale
                    </Button>
                }
            />
            <CardContent sx={{ pt: 0 }}>
                {grades && grades.length > 0 ? (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Score range</TableCell>
                                    <TableCell>Grade</TableCell>
                                    <TableCell>Point</TableCell>
                                    <TableCell>Remarks</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {grades.map((row, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            {row.upperlimit} – {row.lowerlimit}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={row.grade} size="small" color="primary" variant="outlined" />
                                        </TableCell>
                                        <TableCell>{row.gradepoint}</TableCell>
                                        <TableCell>{row.remarks}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                        No grading scale configured yet. Click &quot;Customize scale&quot; to add bands.
                    </Typography>
                )}
            </CardContent>

            <Dialog fullWidth maxWidth="sm" open={openGradesDialog} onClose={() => setOpenGradesDialog(false)}>
                <DialogTitle sx={{ pr: 6 }}>
                    Customize grading scale
                    <IconButton onClick={() => setOpenGradesDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {editingGradingScales.map((band, idx) => (
                        <Grid container spacing={1} key={band.id || idx} alignItems="center" sx={{ mb: 1 }}>
                            <Grid item xs={12} sm={4}>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        label="Upper"
                                        type="number"
                                        size="small"
                                        value={band.upperlimit}
                                        onChange={(e) => updateBand(idx, { upperlimit: e.target.value })}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Lower"
                                        type="number"
                                        size="small"
                                        value={band.lowerlimit}
                                        onChange={(e) => updateBand(idx, { lowerlimit: e.target.value })}
                                        fullWidth
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={4} sm={2}>
                                <TextField
                                    label="Grade"
                                    size="small"
                                    value={band.grade}
                                    onChange={(e) => updateBand(idx, { grade: e.target.value })}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4} sm={2}>
                                <TextField
                                    label="Point"
                                    size="small"
                                    value={band.gradepoint}
                                    onChange={(e) => updateBand(idx, { gradepoint: e.target.value })}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={10} sm={3}>
                                <TextField
                                    label="Remarks"
                                    size="small"
                                    value={band.remarks}
                                    onChange={(e) => updateBand(idx, { remarks: e.target.value })}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={2} sm={1}>
                                <IconButton onClick={() => removeBand(idx)} color="error" size="small">
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))}
                    <Button variant="text" onClick={addGradeBand} sx={{ mt: 1 }}>
                        + Add grade band
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenGradesDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveSchoolGrading} disabled={savingGrades}>
                        {savingGrades ? 'Saving…' : 'Save grading scale'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

const TERMINOLOGY_OPTIONS = ['Term', 'Semester', 'Period', 'Quarter', 'Trimester', 'Session'];

function parseTermEntry(term, months) {
    if (typeof term === 'object' && term !== null) {
        const name = term.name || 'Unnamed';
        const hasRange = term.startMonth && term.startYear && term.endMonth && term.endYear;
        return {
            name,
            legacy: false,
            startLabel: hasRange ? `${months[term.startMonth - 1]} ${term.startYear}` : null,
            endLabel: hasRange ? `${months[term.endMonth - 1]} ${term.endYear}` : null,
            shortRange: hasRange
                ? `${months[term.startMonth - 1]?.slice(0, 3)} ${term.startYear} – ${months[term.endMonth - 1]?.slice(0, 3)} ${term.endYear}`
                : null,
        };
    }
    return {
        name: String(term || ''),
        legacy: true,
        startLabel: null,
        endLabel: null,
        shortRange: null,
    };
}

export function TermFormDialog({
    open,
    onClose,
    editingTermIndex,
    termTerminology,
    setTermTerminology,
    updateTerminology,
    termName,
    setTermName,
    startMonth,
    setStartMonth,
    startYear,
    setStartYear,
    endMonth,
    setEndMonth,
    endYear,
    setEndYear,
    months,
    loading,
    onSubmit,
}) {
    const isEdit = editingTermIndex !== null;
    const startPreview = `${months[startMonth - 1]} ${startYear}`;
    const endPreview = `${months[endMonth - 1]} ${endYear}`;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            {isEdit ? `Edit ${termTerminology}` : `Add ${termTerminology}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Set the name and date range for this academic period.
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} edge="end" aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <form id="term-settings-form" onSubmit={onSubmit}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        What do you call academic periods at your school?
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2.5 }}>
                        {TERMINOLOGY_OPTIONS.map((opt) => (
                            <Chip
                                key={opt}
                                label={opt}
                                clickable
                                color={termTerminology === opt ? 'primary' : 'default'}
                                variant={termTerminology === opt ? 'filled' : 'outlined'}
                                onClick={() => {
                                    setTermTerminology(opt);
                                    updateTerminology(opt);
                                }}
                            />
                        ))}
                    </Stack>

                    <TextField
                        fullWidth
                        label={`${termTerminology} name`}
                        value={termName}
                        onChange={(e) => setTermName(e.target.value)}
                        required
                        placeholder={`e.g. First ${termTerminology} 2024/2025`}
                        sx={{ mb: 2 }}
                    />

                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Start date
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={7}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="term-start-month">Month</InputLabel>
                                <Select
                                    labelId="term-start-month"
                                    value={startMonth}
                                    label="Month"
                                    onChange={(e) => setStartMonth(e.target.value)}
                                >
                                    {months.map((month, index) => (
                                        <MenuItem key={month} value={index + 1}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Year"
                                type="number"
                                value={startYear}
                                onChange={(e) => setStartYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
                                inputProps={{ min: 2000, max: 2100 }}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        End date
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={7}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="term-end-month">Month</InputLabel>
                                <Select
                                    labelId="term-end-month"
                                    value={endMonth}
                                    label="Month"
                                    onChange={(e) => setEndMonth(e.target.value)}
                                >
                                    {months.map((month, index) => (
                                        <MenuItem key={month} value={index + 1}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Year"
                                type="number"
                                value={endYear}
                                onChange={(e) => setEndYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
                                inputProps={{ min: 2000, max: 2100 }}
                            />
                        </Grid>
                    </Grid>

                    <Alert severity="info" icon={<CalendarTodayOutlinedIcon fontSize="small" />} sx={{ mb: 0 }}>
                        Period: <strong>{startPreview}</strong> → <strong>{endPreview}</strong>
                    </Alert>
                </form>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                {!loading ? (
                    <Button type="submit" form="term-settings-form" variant="contained" startIcon={<SaveIcon />}>
                        {isEdit ? `Save ${termTerminology}` : `Add ${termTerminology}`}
                    </Button>
                ) : (
                    <LoadingButton loading variant="contained">
                        Saving…
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    );
}

export function SettingsTermsSection({
    termTerminology,
    setTermTerminology,
    updateTerminology,
    schooldetails_,
    months,
    handleEditTerm,
    removeterm,
    onAddTerm,
    setActiveTerm,
    settingActiveTermIndex,
}) {
    const terms = schooldetails_.length !== 0 && schooldetails_[0].terms ? schooldetails_[0].terms : [];
    const [deleteIndex, setDeleteIndex] = React.useState(null);
    const [showAll, setShowAll] = React.useState(false);

    const currentIndex = schooldetails_.length !== 0 ? getActiveTermIndex(schooldetails_[0]) : -1;
    const currentParsed = currentIndex >= 0 ? parseTermEntry(terms[currentIndex], months) : null;
    const visibleTerms = showAll || terms.length <= 5 ? terms : terms.slice(-5);
    const hiddenCount = terms.length - visibleTerms.length;

    const confirmDelete = () => {
        if (deleteIndex !== null) {
            removeterm(deleteIndex);
            setDeleteIndex(null);
        }
    };

    return (
        <>
            <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, overflow: 'visible' }}>
                <CardHeader
                    avatar={<EventNoteOutlinedIcon color="primary" />}
                    title={`${termTerminology} settings`}
                    subheader="Manage academic periods used across reports, fees, and attendance. Use the check icon to set which period is current."
                    action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={onAddTerm} size="small">
                            Add {termTerminology}
                        </Button>
                    }
                />
                <CardContent sx={{ pt: 0 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        <LabelOutlinedIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                        Period label used in the app
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
                        {TERMINOLOGY_OPTIONS.map((opt) => (
                            <Chip
                                key={opt}
                                label={opt}
                                size="small"
                                clickable
                                color={termTerminology === opt ? 'primary' : 'default'}
                                variant={termTerminology === opt ? 'filled' : 'outlined'}
                                onClick={() => {
                                    setTermTerminology(opt);
                                    updateTerminology(opt);
                                }}
                            />
                        ))}
                    </Stack>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={4}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    CURRENT {termTerminology.toUpperCase()}
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.5 }}>
                                    {currentParsed ? currentParsed.name : `No ${termTerminology.toLowerCase()} yet`}
                                </Typography>
                                {currentParsed?.shortRange ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {currentParsed.shortRange}
                                    </Typography>
                                ) : null}
                            </Paper>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    TOTAL RECORDED
                                </Typography>
                                <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
                                    {terms.length}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    STATUS
                                </Typography>
                                <Chip
                                    label={terms.length ? 'Active period set' : 'Setup required'}
                                    color={terms.length ? 'success' : 'warning'}
                                    size="small"
                                    sx={{ mt: 1 }}
                                />
                            </Paper>
                        </Grid>
                    </Grid>

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            <HistoryOutlinedIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom', mr: 0.5 }} />
                            Timeline
                        </Typography>
                        {hiddenCount > 0 ? (
                            <Button size="small" onClick={() => setShowAll((v) => !v)}>
                                {showAll ? 'Show recent only' : `Show all ${terms.length}`}
                            </Button>
                        ) : null}
                    </Stack>

                    {terms.length > 0 ? (
                        <Box sx={{ position: 'relative', pl: 1 }}>
                            {visibleTerms.map((term, visIdx) => {
                                const index = showAll || terms.length <= 5 ? visIdx : terms.length - visibleTerms.length + visIdx;
                                const parsed = parseTermEntry(term, months);
                                const isCurrent = index === currentIndex;

                                return (
                                    <Box
                                        key={`${parsed.name}-${index}`}
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            mb: 2,
                                            position: 'relative',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 28 }}>
                                            <Box
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '50%',
                                                    bgcolor: isCurrent ? 'primary.main' : 'grey.300',
                                                    color: isCurrent ? '#fff' : 'text.primary',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    zIndex: 1,
                                                }}
                                            >
                                                {index + 1}
                                            </Box>
                                            {visIdx < visibleTerms.length - 1 ? (
                                                <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', minHeight: 16, mt: 0.5 }} />
                                            ) : null}
                                        </Box>

                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                flex: 1,
                                                p: 2,
                                                borderRadius: 2,
                                                borderColor: isCurrent ? 'primary.main' : 'divider',
                                                bgcolor: isCurrent ? 'rgba(25, 118, 210, 0.06)' : 'background.paper',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                        <Typography fontWeight={700}>{parsed.name}</Typography>
                                                        {isCurrent ? (
                                                            <Chip label="Current" size="small" color="success" sx={{ height: 22 }} />
                                                        ) : null}
                                                        {parsed.legacy ? (
                                                            <Chip label="Legacy format" size="small" variant="outlined" sx={{ height: 22 }} />
                                                        ) : null}
                                                    </Stack>
                                                    {parsed.shortRange ? (
                                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75 }}>
                                                            <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                            <Typography variant="body2" color="text.secondary">
                                                                {parsed.shortRange}
                                                            </Typography>
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                                            No date range — edit to add start and end dates.
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Stack direction="row" spacing={0.5} flexShrink={0}>
                                                    {!isCurrent ? (
                                                        <Tooltip title={`Set as current ${termTerminology.toLowerCase()}`}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    disabled={settingActiveTermIndex === index}
                                                                    onClick={() => setActiveTerm && setActiveTerm(index)}
                                                                >
                                                                    <CheckCircleOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    ) : null}
                                                    <Tooltip title={`Edit ${termTerminology}`}>
                                                        <IconButton size="small" onClick={() => handleEditTerm(term, index)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={`Remove ${termTerminology}`}>
                                                        <IconButton size="small" color="error" onClick={() => setDeleteIndex(index)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    </Box>
                                );
                            })}
                        </Box>
                    ) : (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 4,
                                borderRadius: 2,
                                textAlign: 'center',
                                borderStyle: 'dashed',
                                bgcolor: 'grey.50',
                            }}
                        >
                            <EventNoteOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="subtitle1" fontWeight={600}>
                                No {termTerminology.toLowerCase()}s yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                                Add your first {termTerminology.toLowerCase()} so fees, reports, and attendance align to the right period.
                            </Typography>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={onAddTerm}>
                                Add first {termTerminology}
                            </Button>
                        </Paper>
                    )}
                </CardContent>
            </Card>

            <Dialog open={deleteIndex !== null} onClose={() => setDeleteIndex(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Remove {termTerminology}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {deleteIndex !== null && terms[deleteIndex]
                            ? `This will remove "${parseTermEntry(terms[deleteIndex], months).name}" from your school record. This cannot be undone.`
                            : 'This cannot be undone.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteIndex(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete}>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export function SettingsPageShell({ children }) {
    return (
        <Box sx={{ width: '100%', pb: 4 }}>
            <SettingsPageHeader />
            <Container maxWidth="lg" disableGutters sx={{ px: { xs: 1, sm: 2 } }}>
                {children}
            </Container>
        </Box>
    );
}
